import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Icon } from '../../components/Icon'
import { PageHeader } from '../../components/PageHeader'
import _ from 'lodash';
import { Button } from '../../components/Button'
import { Row, Column } from 'react-foundation'
import { connect } from 'react-redux'
import { TextModal } from '../../components/Modal';
import { createStructuredSelector } from 'reselect'
import { selectErrors } from '../../modules/global/selectors';
import { Error } from '../../components/Error';
import { getAccountId } from '../../utils/helpers';
import {
	fetchInvoices, initializeInvoices, resetInvoices,
} from '../../modules/billing/actions';
import {
	makeSelectInvoices, makeSelectInvoicesError, makeSelectFetchingInvoices,
} from '../../modules/billing/selectors';
import { InvoiceUtils } from './InvoiceUtils';
import { ApiRequest } from '../../utils/api';
import { useApiQuery } from '../../utils/apiHooks';

interface Props {
	invoices: any[]
	invoicesFetching: boolean
	errors: any[]
	accountType: string
	invoicesError: any
}

const Invoices = styled.ul`
	list-style-type: none;
	margin-top: 4rem;
	padding-left: 2rem;
	padding-right: 2rem;

	// TODO: Media-queryn pitäis käyttää muuttujien arvoja esim @include breakpoint(large up)
	@media(max-width:800px){
		padding-left: 0.5rem;
		padding-right: 0.5rem;
	}
	.noInvoicesAlert {
		font-size: 1.2rem;
	}
`;

const InvoiceContainer = styled.div`
	min-height: 150px;
	width: 100%;
	border-bottom: 1px solid #eaeaea;
	padding: 0.9em 0 0.7em;
	font-size: 16px;
	line-height: 26px;

	.section-invoice {
		padding-top: 30px;
	}

	.column-icon {
		padding-top: 10px;
	}

	.payment-late {
		color: #f00;
		font-size: 1rem;
		float: left;
	}

	.icon-container {
		text-align: center;

		@media(min-width:800px){
			min-width: 120px;
		}

		a { display: block; }

		.icon {
			margin: auto;
		}
	}

	.alert-icon {
		float: left;
		margin: 20px 10px 50px 10px;

		// TODO: Media-queryn pitäis käyttää muuttujien arvoja esim @include breakpoint(large up)
		@media(min-width:800px){
			margin: 20px 20px 50px 10px;
		}
	}

	.column-due {
		// TODO: Media-queryn pitäis käyttää muuttujien arvoja esim @include breakpoint(large up)
		@media(min-width:1024px) {
			min-width: 230px;
		}
	}

	.date, .paid-amount {
		font-size: 1rem;
		float: left;
		padding-left: 2rem;
		
		&.payment-late {
			padding-left: 0;
		}
	}

	.paid {
		font-size: 1.6rem;
		display: block;
		margin-bottom: 0px;
		line-height: 25px;
		padding-left: 2rem;

		@media(max-width:800px){
			font-size: 1.4rem;
		}
	}

	.billing-period {
		font-size: 1.6rem;
		margin-bottom: 0px;
		line-height: 25px;
		padding-top: 1.3rem;

		@media(max-width:800px){
			font-size: 1.4rem;
		}
	}

	.download-link {
		color: red;
		display: block;
		min-width: 100px;
	}

	.pdf-text {
	}

	.link-invoice-excel {
		line-height: 16px;
	}

	.amount{
		font-size: 60px;
		line-height: 60px;
		padding-left: 2rem;
		font-family: var(--font-family-secondary);
	}

	button {
		outline: none;
	}

	.amountSection{
		padding: 25px 0 0 0;

		// TODO: Media-queryn pitäis käyttää muuttujien arvoja esim @include breakpoint(large up)
		@media(max-width:800px) {
			margin-left: 10px;
		}
	}
`;

const Centralize = styled.div`
	margin: auto;
	text-align: center;
`;

interface AttachmentDownload {
	apiRequest: ApiRequest<any>;
	filename: string
}

export const InvoicesPageImpl: React.FC<Props> = (props: Props) => {

	const dispatch = useDispatch();
	const [ modalContent, setModalContent ] = useState<string|undefined>(undefined);
	const [ lastFetchedPeriodId, setLastFetchedPeriodId ] = useState<string|undefined>(undefined);

	const { data: billingPeriods } = useApiQuery(ApiRequest.getBillingPeriods(getAccountId()));

	useEffect(() => {
		dispatch(initializeInvoices(getAccountId()!));
		return () => dispatch(resetInvoices());
	}, [dispatch]);

	const [ pendingAttachmentDownload, setPendingAttachmentDownload ] = useState<AttachmentDownload|undefined>(undefined)
	useApiQuery(pendingAttachmentDownload?.apiRequest, {
		onSuccess: (data: any) => InvoiceUtils.saveAttachement(data, pendingAttachmentDownload!.filename),
		onError: () => setModalContent('Jotain meni vikaan tiedoston lataamisessa. Ei hätää, yritä myöhemmin uudestaan, tai ota yhteyttä asiakaspalveluumme.'),
		onSettled: () => setPendingAttachmentDownload(undefined)
	});

	useEffect(() => {  // TODO could we calculate setLastFetchedPeriodId from billingPeriods and not store that to a variable
		if (billingPeriods && lastFetchedPeriodId === undefined) {
			if (billingPeriods.length === 1) {
				setLastFetchedPeriodId(billingPeriods[0].id);
			} else if (billingPeriods.length > 1) {
				setLastFetchedPeriodId(billingPeriods[1].id);
			}
		}
	}, [billingPeriods]);  // eslint-disable-line

	const getNextNotFetchedListIndex = () => {
		let index = 0;
		while (index < billingPeriods!.length && billingPeriods![index].id !== lastFetchedPeriodId) {
			index += 1;
		}
		return index + 1;
	};

	// TODO implement invoice fetcing using usePaginatedQuery or useInfiniteQuery
	const fetchNextThreePeriodsInvoices = () => {
		const notFetchedListIndex = getNextNotFetchedListIndex();
		if (notFetchedListIndex + 2 < billingPeriods!.length) {
			setLastFetchedPeriodId(billingPeriods![notFetchedListIndex + 2].id);
			dispatch(fetchInvoices(getAccountId()!, billingPeriods![notFetchedListIndex].id, 3));
		} else if (notFetchedListIndex < billingPeriods!.length) {
			setLastFetchedPeriodId(billingPeriods![billingPeriods!.length - 1].id);
			dispatch(fetchInvoices(getAccountId()!, billingPeriods![notFetchedListIndex].id, billingPeriods!.length - notFetchedListIndex));
		}
	};

	const buttonClassName = (billingPeriods: any[]) => (_.findIndex(billingPeriods, (period) => period.id === lastFetchedPeriodId) === billingPeriods.length - 1) ? 'wide disabled' : 'wide outlined';

	const { invoices, invoicesFetching, errors, accountType } = props;
	const noErrors = errors && errors.length === 0;
	const groupedInvoices = _.groupBy(invoices, (invoice) => invoice.billingPeriodId);

	return (
		<article>
			<Helmet
				title="Laskut"
				meta={[
					{ name: 'description', content: 'Laskut' },
				]}
			/>
			<PageHeader>Laskut</PageHeader>
			{noErrors && invoices && (billingPeriods !== undefined) && (billingPeriods.length > 0) && lastFetchedPeriodId !== undefined && (
				<Row>
					<Invoices>
						{invoices.length === 0 && !invoicesFetching && (
							<Centralize>
								<h4 className={'noInvoicesAlert'}>
									Laskuja ei löytynyt
								</h4>
							</Centralize>
						)}
						{_.map(groupedInvoices, (invoiceList, key) => (
							<div key={key}>
								<InvoiceContainer>
									<Column large={4} small={12}>
										<div className={'billing-period'} >Laskutuskausi&nbsp;{InvoiceUtils.formatDate(invoiceList[0].billingPeriodStartDate)}&nbsp;&#8209;&nbsp;{InvoiceUtils.formatDate(invoiceList[0].billingPeriodEndDate)}</div>
									</Column>
									<Column large={4} small={12}>
										<Button
											className="medium invisible"
											onClick={() => setPendingAttachmentDownload({
												apiRequest: ApiRequest.getInvoiceExcel(
													getAccountId()!,
													invoiceList[0].invoiceNumber, // remove this parameter as it is useless, only used for header, which is not in use any more
													invoiceList[0].billingPeriodId
												),
												filename: `moi-erittely-${invoiceList[0].invoiceNumber}.xlsx`
											})}
											disabled={(pendingAttachmentDownload !== undefined)}
										>
											Kulujen erittely
										</Button>
									</Column>
								</InvoiceContainer>
								{invoiceList.map((invoice, key2) => (
									<div key={key + key2}>
										{(accountType === 'consumer' || (accountType === 'business' && invoice.amount !== 0)) && (
											<InvoiceContainer>
												<Row>
													<Column small={12} large={4} className={'columns section-invoice amountSection'}>
														<span className={'amount'}>{invoice.amount}€</span>
													</Column>
													<Column small={12} large={4} className={'columns section-invoice column-due'}>
														{InvoiceUtils.isPaymentLate(invoice) && invoice.amount > 0 && (
															<Icon className={'alert-icon'} name="small--alert" />
														)}
														<div className={'paid'}>{InvoiceUtils.getPaymentStatusString(invoice)}</div>
														{InvoiceUtils.isPartlyPaid(invoice) &&
															<div>
																<div className={`${InvoiceUtils.isPaymentLate(invoice) ? 'payment-late':''} paid-amount`}>[Maksettu {invoice.paidAmount}€]</div>
																<br />
															</div>
														}
														{invoice.amount > 0 && (
															<div>
																<div className={InvoiceUtils.isPaymentLate(invoice) ? 'payment-late' : 'date'}> Eräpäivä {InvoiceUtils.formatDate(invoice.dueDate)}</div><br />
																<div className={InvoiceUtils.isPaymentLate(invoice) ? 'payment-late' : 'date'}>{`# ${invoice.invoiceNumber}`}</div>
															</div>
														)}
														{invoice.amount <= 0 && (
															<div>
																<div className={'date no-icon'}>{`# ${invoice.invoiceNumber}`}</div>
															</div>
														)}
													</Column>
													<Column small={12} large={4} className={'large-uncentered columns section-invoice end'}>
														<Button
															className="medium invisible"
															onClick={() => setPendingAttachmentDownload({
																apiRequest: ApiRequest.getInvoicePdf(getAccountId()!, invoice.invoiceId, invoice.invoiceNumber),
																filename: `moi-lasku-${invoice.invoiceNumber}.pdf`
															})}
															disabled={(pendingAttachmentDownload !== undefined)}
														>
															Lataa lasku [.pdf]
														</Button>
													</Column>
												</Row>
											</InvoiceContainer>
										)}
									</div>
								))}
							</div>
						)
						)}
					</Invoices>
					{!invoicesFetching && (
						<Button
							className={buttonClassName(billingPeriods)}
							onClick={() => fetchNextThreePeriodsInvoices()}
						>
							Vanhemmat laskut...
						</Button>
					)}
				</Row>
			)}
			{!noErrors && (
				<Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />
			)}
			{((modalContent !== undefined)) && (
				<TextModal
					isOpen={true}
					onRequestClose={() => setModalContent(undefined)}
					content={modalContent}
				/>
			)}
		</article>
	);
}

const mapStateToProps = createStructuredSelector({
	invoices: makeSelectInvoices(),
	invoicesError: makeSelectInvoicesError(),
	invoicesFetching: makeSelectFetchingInvoices(),
	errors: selectErrors,
});

export default connect(mapStateToProps)(InvoicesPageImpl)